/**
 * Pure helpers for the electrical voltage-drop study output (NEC 210.19(A) IN /
 * 215.2(A)(1) IN). The engine emits an optional `voltage_drop_study` (rows +
 * basis) on the raw electrical Output — present only when the opt-in
 * voltage-drop stage ran, absent otherwise (study-off outputs serialize
 * byte-identically). This helper reshapes the study into the flat rows the
 * results view renders, tolerating absent data (returns an empty view).
 *
 * The IN thresholds are informational recommendations, not mandatory limits:
 * branch rows carry a pass/fail verdict (3%), feeder rows are informational-only
 * (no verdict — the combined 5% is a ceiling, not a per-feeder limit). Each row
 * derives its `verdict` from the engine's `exceeded` tri-state so the UI can
 * flag exceedances without re-deriving the rule. Deterministic, no IO —
 * unit-testable as plain functions (mirrors ./emergency).
 */
import type { VoltageDropRow, VoltageDropStudy } from "../api/types";

/** Verdict for a voltage-drop row: within/over the IN threshold, or feeder-informational. */
export type VoltageDropVerdict = "ok" | "exceeded" | "informational";

/** One flattened, display-formatted voltage-drop study row. */
export interface VoltageDropDisplayRow extends VoltageDropRow {
  /** Derived from `exceeded`: true → "exceeded", false → "ok", null/absent → "informational". */
  verdict: VoltageDropVerdict;
  /** Voltage drop as a formatted percent (e.g. "3.50%"). */
  percentText: string;
  /** IN threshold as a formatted percent (e.g. "3%"). */
  thresholdText: string;
  /** Effective impedance formatted (Ω per 1000 ft, e.g. "1.200"). */
  zeffText: string;
}

/** The voltage-drop study split into the rendered rows + basis line. */
export interface VoltageDropView {
  /** Display-formatted rows, preserving the study's order. */
  rows: VoltageDropDisplayRow[];
  /** Human-facing basis line (thresholds + citations); empty when off. */
  basis: string;
  /** True when there is nothing to render (stage off / empty). */
  isEmpty: boolean;
}

/**
 * Derive the verdict from the engine's `exceeded` tri-state: `true` → the row
 * exceeds its IN threshold, `false` → within, `null`/absent → an informational
 * feeder row (no pass/fail verdict).
 */
export function voltageDropVerdict(exceeded: boolean | null | undefined): VoltageDropVerdict {
  if (exceeded === true) return "exceeded";
  if (exceeded === false) return "ok";
  return "informational";
}

/**
 * Normalize the raw voltage-drop study into the flat view the results view
 * renders. `voltage_drop_study` is absent/null when the voltage-drop stage did
 * not run, so a missing input yields an empty view with `isEmpty: true` — the
 * UI shows a placeholder instead of an empty table.
 */
export function voltageDropStudyView(
  study: VoltageDropStudy | null | undefined,
): VoltageDropView {
  const rawRows: VoltageDropRow[] = study && Array.isArray(study.rows) ? study.rows : [];
  const rows = rawRows.map((r) => ({
    ...r,
    verdict: voltageDropVerdict(r.exceeded),
    percentText: `${r.percent.toFixed(2)}%`,
    thresholdText: `${r.threshold_pct}%`,
    zeffText: r.zeff_per_1000ft.toFixed(3),
  }));

  return {
    rows,
    basis: study?.basis ?? "",
    isEmpty: rows.length === 0,
  };
}
